import React, { useEffect, useState, useRef } from 'react';
import api from '../../api';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import SuccessModal from '../../components/common/SuccessModal';
import FailModal from '../../components/common/FailModal';
import { usePagination } from '../../hooks/usePagination';
import { GenericView, SearchInput } from '../../components/common/table';

const FindingCategory = () => {
  // Actions dropdown refs for manual Bootstrap Dropdown initialization
  const actionsToggleRef = useRef(null);
  const actionsContainerRef = useRef(null);
  const actionsDropdownInstanceRef = useRef(null);
  const [isActionsOpen, setIsActionsOpen] = useState(false);

  useEffect(() => {
    const toggleEl = actionsToggleRef.current;
    const containerEl = actionsContainerRef.current;
    if (!toggleEl || !containerEl) return;
    if (typeof window !== 'undefined' && window.bootstrap?.Dropdown) {
      const instance = new window.bootstrap.Dropdown(toggleEl, { autoClose: true });
      actionsDropdownInstanceRef.current = instance;
      const onShow = () => setIsActionsOpen(true);
      const onHide = () => setIsActionsOpen(false);
      containerEl.addEventListener('show.bs.dropdown', onShow);
      containerEl.addEventListener('hide.bs.dropdown', onHide);
      return () => {
        containerEl.removeEventListener('show.bs.dropdown', onShow);
        containerEl.removeEventListener('hide.bs.dropdown', onHide);
        try { instance.dispose(); } catch {}
        actionsDropdownInstanceRef.current = null;
      };
    }
  }, []);

  const pagination = usePagination(10);

  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const handleSearchInputChange = (value) => {
    setSearch(value);
    setSearchTerm(value);
  };

  const handleSearchSubmit = () => {
    setAppliedSearchTerm(searchTerm);
    pagination.setPage(0);
  };

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
  });
  const [errors, setErrors] = useState({});

  // Modal state
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showFailModal, setShowFailModal] = useState(false);
  const [failMessage, setFailMessage] = useState('');

  // Fetch all finding categories (no pagination from backend, do client-side)
  const fetchCategories = async (searchValue = appliedSearchTerm) => {
    try {
      setIsLoading(true);
      const response = await api.get('/audit/finding-categories');
      const data = response.data || [];
      const filtered = searchValue
        ? data.filter(
            (item) =>
              item.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
              item.code?.toLowerCase().includes(searchValue.toLowerCase())
          )
        : data;
      setCategories(filtered);
      pagination.setTotalPages(Math.max(1, Math.ceil(filtered.length / pagination.size)));
      pagination.setTotal(filtered.length);
      pagination.setCurrentPageItems(
        Math.min(pagination.size, filtered.length - pagination.page * pagination.size)
      );
    } catch (error) {
      let errorMessage = 'Failed to fetch finding categories.';
      let technicalDetails = '';
      if (error.response) {
        switch (error.response.status) {
          case 403:
            errorMessage = 'You do not have permission to view finding categories.';
            technicalDetails = 'Required role or permission is missing';
            break;
          case 500:
            errorMessage = 'Server encountered an error while fetching finding categories.';
            technicalDetails = error.response.data?.message || 'Internal server error occurred';
            break;
          default:
            errorMessage = 'An error occurred while fetching finding categories.';
            technicalDetails = `Server responded with status: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Unable to connect to the server.';
        technicalDetails = 'Network error or server is not responding';
      } else {
        errorMessage = 'An unexpected error occurred.';
        technicalDetails = error.message;
      }
      setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
      setShowFailModal(true);
      setCategories([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories(appliedSearchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.size, appliedSearchTerm]);


  // Open modal for create
  const handleCreate = () => {
    setFormData({ code: '', name: '', description: '' });
    setEditingId(null);
    setErrors({});
    const modal = new window.bootstrap.Modal(document.getElementById('modal-finding-category'));
    modal.show();
  };

  // Open modal for edit
  const handleEdit = (item) => {
    setFormData({
      code: item.code || '',
      name: item.name || '',
      description: item.description || '',
    });
    setEditingId(item.id);
    setErrors({});
    const modal = new window.bootstrap.Modal(document.getElementById('modal-finding-category'));
    modal.show();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.code.trim()) newErrors.code = 'Code is required';
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const payload = {
        code: formData.code.trim(),
        name: formData.name.trim(),
        description: formData.description.trim(),
      };

      if (editingId) {
        await api.put(`/audit/finding-categories/${editingId}`, payload);
        setSuccessMessage('Finding Category has been successfully updated.');
      } else {
        await api.post('/audit/finding-categories', payload);
        setSuccessMessage('Finding Category has been successfully added.');
      }

      setEditingId(null);
      setFormData({ code: '', name: '', description: '' });
      setErrors({});
      await fetchCategories(appliedSearchTerm);

      const modalEl = document.getElementById('modal-finding-category');
      const bootstrapModal = window.bootstrap?.Modal.getInstance(modalEl);
      if (bootstrapModal) {
        bootstrapModal.hide();
        setTimeout(() => setShowSuccessModal(true), 300);
      } else {
        setShowSuccessModal(true);
      }
    } catch (error) {
      let errorMessage = 'Failed to save finding category.';
      let technicalDetails = '';
      if (error.response) {
        switch (error.response.status) {
          case 400:
            errorMessage = 'Invalid data provided.';
            technicalDetails = error.response.data?.message || 'The server rejected the request due to invalid data';
            break;
          case 403:
            errorMessage = 'You do not have permission to perform this action.';
            technicalDetails = 'Required role or permission is missing';
            break;
          case 409:
            errorMessage = 'A finding category with this code already exists.';
            technicalDetails = `Duplicate entry for code: "${formData.code}"`;
            break;
          case 500:
            errorMessage = 'Server encountered an error while saving.';
            technicalDetails = error.response.data?.message || 'Internal server error occurred';
            break;
          default:
            errorMessage = 'An error occurred while saving the finding category.';
            technicalDetails = `Server responded with status: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Unable to connect to the server.';
        technicalDetails = 'Network error or server is not responding';
      } else {
        errorMessage = 'An unexpected error occurred.';
        technicalDetails = error.message;
      }
      setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
      setShowFailModal(true);
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteTargetId(id);
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;
    try {
      await api.delete(`/audit/finding-categories/${deleteTargetId}`);
      setDeleteTargetId(null);
      setShowDeleteConfirmation(false);
      await fetchCategories(appliedSearchTerm);
      setSuccessMessage('Finding Category has been successfully deleted.');
      setShowSuccessModal(true);
    } catch (error) {
      let errorMessage = 'Failed to delete finding category.';
      let technicalDetails = '';
      if (error.response) {
        switch (error.response.status) {
          case 403:
            errorMessage = 'You do not have permission to delete this item.';
            technicalDetails = 'Required role or permission is missing';
            break;
          case 404:
            errorMessage = 'Finding category not found.';
            technicalDetails = 'The item may have already been deleted';
            break;
          case 500:
            errorMessage = 'Server error while deleting item.';
            technicalDetails = error.response.data?.message || 'Internal server error during deletion';
            break;
          default:
            errorMessage = 'An error occurred while deleting the finding category.';
            technicalDetails = `Server responded with status: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Unable to connect to the server.';
        technicalDetails = 'Network error or server is not responding';
      } else {
        errorMessage = 'An unexpected error occurred during deletion.';
        technicalDetails = error.message;
      }
      setShowDeleteConfirmation(false);
      setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
      setShowFailModal(true);
    }
  };

  // Modal cleanup
  useEffect(() => {
    const modal = document.getElementById('modal-finding-category');
    const handleHide = () => {
      setFormData({ code: '', name: '', description: '' });
      setEditingId(null);
      setErrors({});
    };
    modal?.addEventListener('hidden.bs.modal', handleHide);
    return () => {
      modal?.removeEventListener('hidden.bs.modal', handleHide);
    };
  }, []);

  // Paginated data (client-side)
  return (
    <>
      {/* Page header */}
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <h2 className="page-title">Finding Category</h2>
            </div>
            <div className="col-auto ms-auto d-print-none">
              <div className="btn-list">
                <button
                  className="btn btn-primary d-none d-sm-inline-block"
                  onClick={handleCreate}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="icon"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    stroke="currentColor"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Page body */}
      <div className="page-body">
        <div className="container-xl">
          <div className="card">
            <div className="card-body">
              <GenericView
                data={categories.slice(pagination.page * pagination.size, (pagination.page + 1) * pagination.size)}
                columns={[
                  { key: 'code', label: 'Code', sortable: true, render: (value) => value ? <span className="badge bg-blue-lt">{value}</span> : '-' },
                  { key: 'name', label: 'Name', sortable: true, render: (value) => value ? <strong>{value}</strong> : '-' },
                  { key: 'description', label: 'Description', sortable: true, render: (value) => value || '-' },
                  { key: 'isActive', label: 'Status', sortable: true, render: (value) => value !== false ? <span className="badge bg-success-lt text-success">Active</span> : <span className="badge bg-danger-lt text-danger">Inactive</span> },
                ]}
                isLoading={isLoading}
                emptyStateMessage="No finding categories found"
                checkboxSelection={true}
                selectedRows={selectedIds}
                onSelectRow={(itemId, isSelected) => {
                  if (isSelected) {
                    setSelectedIds([...selectedIds, itemId]);
                  } else {
                    setSelectedIds(selectedIds.filter(id => id !== itemId));
                  }
                }}
                onSelectAllRows={(itemIds) => {
                  if (Array.isArray(itemIds) && itemIds.length > 0) {
                    setSelectedIds([...new Set([...selectedIds, ...itemIds])]);
                  } else {
                    setSelectedIds([]);
                  }
                }}
                onEdit={(item) => {
                  setSelectedIds([item.id]);
                  handleEdit(item);
                }}
                onDelete={(item) => {
                  setSelectedIds([item.id]);
                  handleDeleteClick(item.id);
                }}
                searchComponent={
                  <div className="d-flex gap-2">
                    <SearchInput
                      value={searchTerm}
                      onChange={handleSearchInputChange}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSearchSubmit();
                        }
                      }}
                      placeholder="Search by name or code..."
                      uniqueId="finding-category-search"
                    />
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleSearchSubmit}
                    >
                      Search
                    </button>
                  </div>
                }
                actionButtons={
                  <div className="d-flex gap-2">
                    <div ref={actionsContainerRef} className="dropdown">
                      <a
                        ref={actionsToggleRef}
                        href="#"
                        className="btn dropdown-toggle"
                        data-bs-toggle="dropdown"
                        aria-expanded={isActionsOpen}
                        onClick={(e) => {
                          e.preventDefault();
                          const inst = actionsDropdownInstanceRef.current;
                          if (inst?.toggle) inst.toggle();
                          else setIsActionsOpen(prev => !prev);
                        }}
                      >Actions</a>
                      <div className="dropdown-menu">
                        <a className="dropdown-item" href="#" onClick={(e) => {
                          e.preventDefault();
                          const item = categories.find(c => c.id === selectedIds[0]);
                          if (item) handleEdit(item);
                        }}
                          style={{ color: selectedIds.length === 1 ? "" : "#ccc", pointerEvents: selectedIds.length === 1 ? "auto" : "none" }}>
                          Edit
                        </a>
                        <a className="dropdown-item" href="#" onClick={(e) => {
                          e.preventDefault();
                          if (selectedIds.length > 0) handleDeleteClick(selectedIds[0]);
                        }}
                          style={{ color: selectedIds.length ? "" : "#ccc", pointerEvents: selectedIds.length ? "auto" : "none" }}>
                          Delete
                        </a>
                      </div>
                    </div>
                  </div>
                }
                pagination={{
                  currentPage: pagination.page + 1,
                  totalRows: pagination.total,
                  rowsPerPage: pagination.size,
                  onPageChange: (page) => pagination.setPage(page - 1),
                  onRowsPerPageChange: (size) => {
                    pagination.setSize(size);
                    pagination.setPage(0);
                  }
                }}
                pageSizeOptions={[10, 25, 50, 100]}
                uniqueId="finding-category-table"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Modal for add/edit */}
      <div
        className="modal modal-blur fade"
        id="modal-finding-category"
        tabIndex="-1"
        role="dialog"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {editingId ? 'Edit Finding Category' : 'Add New Finding Category'}
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label required">Code</label>
                  <input
                    type="text"
                    className={`form-control ${errors.code ? 'is-invalid' : ''}`}
                    name="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    placeholder="Enter category code (e.g. CAT-01)"
                  />
                  {errors.code && (
                    <div className="invalid-feedback">{errors.code}</div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label required">Name</label>
                  <input
                    type="text"
                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter category name"
                  />
                  {errors.name && (
                    <div className="invalid-feedback">{errors.name}</div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    name="description"
                    rows="3"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Enter description (optional)"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-link link-secondary btn-3"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary ms-auto">
                  {editingId ? 'Update' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => {
          setShowDeleteConfirmation(false);
          setDeleteTargetId(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Confirmation"
        message="Are you sure you want to delete this finding category? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Success"
        message={successMessage}
        buttonText="OK"
      />

      {/* Fail Modal */}
      <FailModal
        isOpen={showFailModal}
        onClose={() => setShowFailModal(false)}
        title="Operation Failed"
        message={failMessage}
        buttonText="Close"
      />
    </>
  );
};

export default FindingCategory;
